import { View, StyleSheet, Modal } from "react-native";
import { BottomButtons, LightBtn } from "../../../assets";
import { useEffect, useRef, useState } from "react";
import { useDevice } from "../../../utils/hooks/useDevice";
import { TopSectionMobile } from "../Profile/TopSectionMobile";
import { TopSectionDesktop } from "../Profile/TopSectionDesktop";
import MainContent from "../Profile/MainContentProfile";
import { ButtonCard } from "../Profile/ButtonCard";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../navigator";
import { useTranslation } from "react-i18next";
import ProfileEditModal from "../Profile/ProfileEditModal";
import { analyticsEvents } from "../../../services/firebase/analytics-events.service";
import { deltaDNAService } from "../../../services/deltaDNA/deltadna.service";

interface IProps {
  onClose: () => void;
  player?: {
    rank: number;
    playerId: string;
    username: string;
    avatarFileId: string | null;
    totalChips: number;
    level: number;
    isVip: boolean;
  };
}
type Nav = NativeStackNavigationProp<RootStackParamList>;

function ProfileModal({ onClose, player }: IProps) {
  const { t } = useTranslation();
  const [isOpenProfileEditModal, setIsOpenProfileEditModal] = useState(false);
  analyticsEvents.trackOpenProfile();
  const { navigate } = useNavigation<Nav>();
  const { isTablet, isDesktop, isSm, isMobile } = useDevice();

  const isPortrait = isMobile || isTablet || isSm;

  const [activeTab, setActiveTab] = useState<
    "main" | "chips" | "vip" | "tournaments"
  >("main");

  return (
    <>
      <Modal transparent visible>
        <View
          style={[
            styles.modalOverlay,
            { minWidth: isDesktop ? 600 : 300, gap: isDesktop ? 10 : 6 },
          ]}
        >
          <View
            style={[
              styles.modalContainer,
              { width: isPortrait ? "85%" : "60%" },
            ]}
          >
            {isPortrait ? (
              <>
                <TopSectionMobile
                  onClose={onClose}
                  visible={false}
                  onOpenProfileEditModal={() => setIsOpenProfileEditModal(true)}
                  player={player}
                />
              </>
            ) : (
              <>
                <TopSectionDesktop
                  onClose={onClose}
                  visible={false}
                  onOpenProfileEditModal={() => setIsOpenProfileEditModal(true)}
                  player={player}
                />
              </>
            )}
            <MainContent activeTab={activeTab} player={player} />
            <View style={styles.bottomButtons}>
              <ButtonCard
                activeImage={LightBtn}
                inactiveImage={BottomButtons}
                label={t("T_SITE_PROFILE_BUY")}
                onPress={() => {
                  onClose();
                  navigate("BuyPackages", { activeTab: "chips" });
                }}
                active={activeTab === "chips"}
              />
              <ButtonCard
                activeImage={LightBtn}
                inactiveImage={BottomButtons}
                label={t("T_SITE_BUY_VIP")}
                onPress={() => {
                  onClose();
                  navigate("BuyPackages", { activeTab: "vip" });
                }}
                active={activeTab === "vip"}
              />
              <ButtonCard
                activeImage={LightBtn}
                inactiveImage={BottomButtons}
                label={t("T_SITE_RANKING_TOURNAMENTS")}
                onPress={() => setActiveTab("tournaments")}
                active={activeTab === "tournaments"}
              />
            </View>
          </View>
          {isOpenProfileEditModal && (
            <ProfileEditModal
              onClose={() => setIsOpenProfileEditModal(false)}
            />
          )}
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  fullWidthAndHeight: {
    width: "100%",
    height: "100%",
  },
  fullWidth: {
    width: "100%",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.7)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    maxHeight: "85%",
    backgroundColor: "#112345",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#00f0ff",
    position: "relative",
    padding: 2,
    paddingTop: 0,
    marginTop: 20,
  },
  container: {
    padding: 16,
  },
  photoButton: {
    zIndex: 1,
    position: "absolute",
    width: 50,
    height: 50,
    top: -24,
    right: 80,
  },
  editButton: {
    zIndex: 1,
    position: "absolute",
    width: 50,
    height: 50,
    top: -24,
    right: 145,
  },
  closeButton: {
    zIndex: 1,
    position: "absolute",
    width: 50,
    height: 50,
    top: -24,
    right: 17,
  },
  iconButtonImage: {
    width: 50,
    height: 50,
  },
  avatarWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    width: 120,
    marginLeft: -10,
  },
  avatarBg: {
    width: 130,
    height: 100,
    position: "relative",
  },
  avatarImage: {
    width: 130,
    height: 76,
    position: "absolute",
    top: 13,
    left: -5,
  },
  scoreBadge: {
    width: 38,
    height: 38,
    position: "absolute",
    top: 30,
    left: 88,
  },
  scoreText: {
    width: 38,
    height: 38,
    position: "absolute",
    top: 36,
    left: 98,
    color: "#fff",
    fontSize: 18,
    fontWeight: "600",
  },
  statsHeader: {
    position: "relative",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 24,
    width: "52%",
    height: 20,
    marginLeft: "24%",
  },
  statsItem: {
    flexDirection: "row",
    alignItems: "center",
    width: 160,
  },
  statsIcon: {
    width: 34,
    height: 34,
  },
  statsText: {
    color: "white",
    fontSize: 18,
    marginLeft: 5,
    fontWeight: "bold",
  },
  nameWrapper: {
    position: "relative",
  },
  nameBg: {
    width: 224,
    height: 38,
  },
  nameText: {
    position: "absolute",
    top: 4,
    left: 60,
    flex: 1,
    color: "white",
    fontSize: 20,
    fontWeight: "500",
    marginLeft: 3,
    width: 120,
  },
  divider: {
    marginTop: 10,
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  bottomButtons: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: -5,

    //   marginTop: isPortrait ? 10 : 20,
  },
});

export default ProfileModal;
